import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiImage, FiPlus, FiEdit3, FiTrash2, FiX } from "react-icons/fi";

const API_URL = process.env.REACT_APP_API_URL;

const EventGallery = () => {
    const [gallery, setGallery] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editIndex, setEditIndex] = useState(null);

    const [formData, setFormData] = useState({
        image: null,
        title: "",
        description: "",
        date: "",
        venue: "",
    });

    useEffect(() => {
        fetchGallery();
    }, []);

    const fetchGallery = async () => {
        try {
            const res = await fetch(`${API_URL}/api/gallery`);
            const data = await res.json();

            if (data.success) {
                setGallery(data.data);
            }
        } catch (err) {
            console.error("Fetch Gallery Error:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "image" && files?.[0]) {
            setFormData((prev) => ({
                ...prev,
                image: files[0],
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (
            !formData.image ||
            !formData.title ||
            !formData.description ||
            !formData.date ||
            !formData.venue
        ) {
            alert("Fill all fields");
            return;
        }

        try {
            const payload = new FormData();

            payload.append("image", formData.image);
            payload.append("title", formData.title);
            payload.append("description", formData.description);
            payload.append("date", formData.date);
            payload.append("venue", formData.venue);

            const res = await fetch(`${API_URL}/api/gallery`, {
                method: "POST",
                body: payload,
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Failed to create gallery item");
            }

            if (data.success) {
                await fetchGallery();

                setFormData({
                    image: null,
                    title: "",
                    description: "",
                    date: "",
                    venue: "",
                });

                setShowForm(false);
                setEditIndex(null);
            }
        } catch (error) {
            console.error("Create Gallery Error:", error);
            alert(error.message || "Failed to create gallery item");
        }
    };

    const handleEdit = (index) => {
        setFormData(gallery[index]);
        setEditIndex(index);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        await fetch(`${API_URL}/api/gallery/${id}`, {
            method: "DELETE",
        });

        setGallery(gallery.filter((item) => item._id !== id));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
            <div className="max-w-7xl mx-auto space-y-8">
                <div className="bg-white rounded-3xl p-6 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center">
                            <FiImage className="text-white text-xl" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold">Event Gallery</h1>
                            <p className="text-gray-500 text-sm">Manage gallery images</p>
                        </div>
                    </div>

                    <button
                        onClick={() => setShowForm(true)}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 py-2 rounded-xl flex items-center gap-2"
                    >
                        <FiPlus /> Add Image
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {gallery.map((item, index) => (
                        <motion.div
                            key={item._id || index}
                            whileHover={{ scale: 1.03 }}
                            className="bg-white rounded-3xl overflow-hidden shadow-md"
                        >
                            <img
                                src={
                                    item.image?.startsWith("http")
                                        ? item.image
                                        : `${API_URL}${item.image}`
                                }
                                alt={item.title}
                                className="w-full h-52 object-cover"
                            />

                            <div className="p-4 space-y-2">
                                <h3 className="font-semibold">{item.title}</h3>
                                <p className="text-sm text-[#fd3e3d]">{item.date}</p>

                                <p className="text-sm text-gray-600">
                                    {item.description?.length > 60
                                        ? item.description.slice(0, 60) + "..."
                                        : item.description}
                                </p>

                                <p className="text-xs text-gray-500">📍 {item.venue}</p>

                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={() => handleEdit(index)}
                                        className="bg-blue-500 text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1"
                                    >
                                        <FiEdit3 /> Edit
                                    </button>

                                    <button
                                        onClick={() => handleDelete(item._id)}
                                        className="bg-red-500 text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1"
                                    >
                                        <FiTrash2 /> Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                {gallery.length === 0 && (
                    <div className="text-center py-16 text-gray-500">
                        No gallery items yet
                    </div>
                )}
            </div>

            <AnimatePresence>
                {showForm && (
                    <motion.div
                        className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.9 }}
                        >
                            <div className="p-6 space-y-4">
                                <div className="flex justify-between items-center">
                                    <h2 className="font-bold text-lg">
                                        {editIndex !== null ? "Edit Image" : "Add Image"}
                                    </h2>
                                    <button onClick={() => setShowForm(false)}>
                                        <FiX />
                                    </button>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-gray-700">
                                            Event Image
                                        </label>

                                        <div className="relative border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 transition">
                                            <input
                                                type="file"
                                                name="image"
                                                accept="image/*"
                                                onChange={handleChange}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                required
                                            />

                                            <p className="text-sm text-gray-600 font-medium">
                                                Click to upload image
                                            </p>
                                            <p className="text-xs text-gray-400">
                                                PNG, JPG, GIF up to 10MB
                                            </p>
                                        </div>

                                        {formData.image instanceof File && (
                                            <img
                                                src={URL.createObjectURL(formData.image)}
                                                alt="preview"
                                                className="mt-3 w-32 h-32 object-cover rounded-lg mx-auto"
                                            />
                                        )}
                                    </div>

                                    <input
                                        type="text"
                                        name="title"
                                        placeholder="Event Name"
                                        value={formData.title}
                                        onChange={handleChange}
                                        className="w-full p-3 border rounded-xl"
                                        required
                                    />

                                    <textarea
                                        name="description"
                                        placeholder="Event Description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="w-full p-3 border rounded-xl"
                                        required
                                    />

                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        className="w-full p-3 border rounded-xl"
                                        required
                                    />

                                    <input
                                        type="text"
                                        name="venue"
                                        placeholder="Venue"
                                        value={formData.venue}
                                        onChange={handleChange}
                                        className="w-full p-3 border rounded-xl"
                                        required
                                    />

                                    <button
                                        type="submit"
                                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl"
                                    >
                                        {editIndex !== null ? "Update" : "Create"}
                                    </button>
                                </form>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default EventGallery;